import { useNav } from '../navigation.js';

/**
 * VISUAL-PROTOTYPE ONLY — the "Today, reconciled" payoff surface from the
 * liquid-glass exploration. The delta band is the one place the day's change
 * of state is presented as a whole; the record of who decided what stays a
 * plain solid card underneath it, because the payoff is glass and the audit
 * is not.
 *
 * Figures are illustrative concept data, labelled as such on the page. A
 * production version would read start-of-day snapshots and the attribution
 * feed; neither exists yet, and inventing them silently is exactly what this
 * product's four-state rules forbid.
 */

const DELTAS = [
  { label: 'Exceptions open', from: 14, to: 3, delta: '−11 today' },
  { label: 'Unstaffed visits', from: 2, to: 0, delta: 'Cleared · Coverage Agent' },
  { label: 'Visits at risk', from: 5, to: 1, delta: '−4 · 1 rescheduled' },
  { label: 'Documentation open', from: 23, to: 9, delta: '−14 · 12 reminders sent' },
];

const CHANGES = [
  {
    at: '8:14 AM',
    text: 'Coverage Agent staffed 2 visits — outreach approved by Alex Rivera, accepted by Maya Chen and Jordan Ellis.',
    href: '/agents/runs/run_cov_done',
  },
  {
    at: '8:31 AM',
    text: 'Documentation Follow-Up sent 12 reminders — approved by Alex Rivera. 9 notes closed by 5:00 PM.',
    href: '/agents/runs/run_docs_done',
  },
  {
    at: '11:02 AM',
    text: '3 clock exceptions resolved in Decisions — 2 corrections filed, originals kept.',
    href: '/time/exceptions',
  },
  {
    at: '4:45 PM',
    text: '4 at-risk visits confirmed delivered, 1 rescheduled with the client — noted in Visits.',
    href: '/visits',
  },
];

export function TodayReconciled() {
  const navigate = useNav();
  return (
    <div className="rn-col recon-page">
      <div className="recon-head">
        <h1 className="recon-title">Today, reconciled</h1>
        <span className="rn-muted recon-asof">
          as of 6:00 PM · concept preview, illustrative figures
        </span>
        <button
          type="button"
          className="rn-link recon-audit"
          onClick={() => navigate('/compliance/audit')}
        >
          Audit →
        </button>
      </div>

      <section className="recon-band" aria-label="What changed today">
        <div className="recon-grid">
          {DELTAS.map((tile) => (
            <div key={tile.label} className="recon-tile">
              <div className="recon-ov">{tile.label}</div>
              <div className="recon-figures">
                <span className="recon-from">{tile.from}</span>
                <svg
                  className="recon-arrow"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
                <span className="recon-to">{tile.to}</span>
              </div>
              <span className="recon-delta">{tile.delta}</span>
            </div>
          ))}
        </div>
        <div className="recon-band-foot">
          <span className="recon-scope">Counts your team, not the whole organization.</span>
          <span className="recon-chip">every figure grounded · audit on every change</span>
        </div>
      </section>

      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">What changed — and who decided it</h2>
          <span className="rn-muted">The payoff is glass; the record is not.</span>
        </div>
        <div className="recon-rows">
          {CHANGES.map((row) => (
            <div key={row.at} className="recon-row">
              <span className="recon-time">{row.at}</span>
              <span className="recon-text">{row.text}</span>
              <button type="button" className="rn-link" onClick={() => navigate(row.href)}>
                Open
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
